import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronLeft, User, Code, Activity, FileText, Bell } from "lucide-react";
import AppHeader from "@/components/AppHeader";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import UserAnalyticsPanel from "@/components/admin/UserAnalyticsPanel";
import CodeManagementPanel from "@/components/admin/CodeManagementPanel";
import AppLogsPanel from "@/components/admin/AppLogsPanel";
import UserActivityPanel from "@/components/admin/UserActivityPanel";
import NotificationPanel from "@/components/admin/NotificationPanel";
import { useAuth } from "@/contexts/AuthContext";
import { useTranslation } from "react-i18next";

const AdminPanelPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useTranslation();
  const [activeTab, setActiveTab] = useState("users");

  // Debug admin status
  
  

  // Redirect if user is not an admin
  if (user && !user.is_dev_admin) {
    navigate("/settings");
    return null;
  }

  return (
    <div className="page-container bg-gray-25">
      <AppHeader
        title={t('admin.admin_panel')}
        leftAction={
          <Button variant="ghost" size="icon" onClick={() => navigate("/settings")} className="text-gray-700">
            <ChevronLeft className="h-5 w-5" />
          </Button>
        }
      />

      <main className="main-content">
        <div className="p-4 pb-24">
          <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="mb-5 flex w-full gap-1 overflow-x-auto bg-gray-100 border border-gray-200 rounded-md p-1">
              <TabsTrigger value="users" className="min-w-[74px] flex flex-col items-center py-2 px-1 data-[state=active]:bg-brand-600 data-[state=active]:text-white rounded-md">
                <User className="h-4 w-4 mb-0.5" />
                <span className="text-[10px] font-medium">{t('admin.users')}</span>
              </TabsTrigger>
              <TabsTrigger value="codes" className="min-w-[74px] flex flex-col items-center py-2 px-1 data-[state=active]:bg-brand-600 data-[state=active]:text-white rounded-md">
                <Code className="h-4 w-4 mb-0.5" />
                <span className="text-[10px] font-medium">{t('admin.codes')}</span>
              </TabsTrigger>
              <TabsTrigger value="onesignal" className="min-w-[90px] flex flex-col items-center py-2 px-1 data-[state=active]:bg-brand-600 data-[state=active]:text-white rounded-md">
                <Bell className="h-4 w-4 mb-0.5" />
                <span className="text-[10px] font-medium">{t('admin.onesignal')}</span>
              </TabsTrigger>
              <TabsTrigger value="logs" className="min-w-[74px] flex flex-col items-center py-2 px-1 data-[state=active]:bg-brand-600 data-[state=active]:text-white rounded-md">
                <FileText className="h-4 w-4 mb-0.5" />
                <span className="text-[10px] font-medium">{t('admin.reports')}</span>
              </TabsTrigger>
              <TabsTrigger value="activity" className="min-w-[74px] flex flex-col items-center py-2 px-1 data-[state=active]:bg-brand-600 data-[state=active]:text-white rounded-md">
                <Activity className="h-4 w-4 mb-0.5" />
                <span className="text-[10px] font-medium">{t('admin.activity')}</span>
              </TabsTrigger>
            </TabsList>

            <TabsContent value="users" className="m-0">
              <UserAnalyticsPanel />
            </TabsContent>

            <TabsContent value="codes" className="m-0">
              <CodeManagementPanel />
            </TabsContent>

            <TabsContent value="onesignal" className="m-0">
              <NotificationPanel />
            </TabsContent>

            <TabsContent value="logs" className="m-0">
              <AppLogsPanel />
            </TabsContent>

            <TabsContent value="activity" className="m-0">
              <UserActivityPanel />
            </TabsContent>
          </Tabs>

          <div className="mt-8 pb-4">
            <Button
              variant="outline"
              className="w-full border-gray-200 text-gray-700 hover:bg-gray-50"
              onClick={() => navigate("/settings")}
            >
              <ChevronLeft className="mr-2 h-4 w-4" />
              {t('common.return_to_settings')}
            </Button>
          </div>
        </div>
      </main>
    </div>
  );
};

export default AdminPanelPage; 